/**
 * SEO.tsx — динамическое управление meta-тегами.
 *
 * Приоритет: props > page_meta[pathname] > глобальные настройки > дефолты.
 * 
 * Каждый монтинг/смена пути загружает свежие настройки с бэкенда без кеширования.
 * Заменяет, а не добавляет meta-теги, чтобы не было дубликатов.
 * 
 * Версия: 2.0 (SSR-ready)
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

// === ТИПЫ ===

interface SeoSettings {
  meta_title: string
  meta_description: string
  meta_keywords: string
  site_url: string
  og_image_url: string
  robots_txt: string
  page_meta: string | Record<string, Record<string, string>>
}

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  canonical?: string
  noIndex?: boolean
}

// === ТЕГИ КОТОРЫЕ ЕСТЬ В index.html — их нужно заменять, не дублировать ===
const STATIC_TAGS = [
  'title',
  'description',
  'robots',
  'canonical',
  'og:locale',
  'og:type',
  'og:title',
  'og:description',
  'og:url',
  'og:updated_time',
  'og:image',
  'og:image:secure_url',
  'og:image:width',
  'og:image:height',
  'og:image:alt',
  'og:image:type',
  'og:site_name',
  'twitter:card',
  'twitter:title',
  'twitter:description',
  'twitter:image',
  'twitter:label1',
  'twitter:data1',
  'twitter:label2',
  'twitter:data2',
]

export default function SEO(props: SEOProps) {
  const { pathname } = useLocation()
  const [settings, setSettings] = useState<SeoSettings | null>(null)

  // Загружаем настройки с бэкенда при монтировании и смене пути
  useEffect(() => {
    let cancelled = false
    fetch('/api/seo/settings')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data) {
          if (typeof data.page_meta === 'string') {
            try { data.page_meta = JSON.parse(data.page_meta) } catch { data.page_meta = {} }
          }
          setSettings(data)
        }
      })
      .catch(() => { /* тихо падаем */ })
    return () => { cancelled = true }
  }, [pathname])

  // Применяем meta-теги
  useEffect(() => {
    if (!settings) return

    // === Парсим page_meta для текущего пути ===
    const pageMeta = (settings.page_meta || {}) as Record<string, Record<string, string>>
    const page = pageMeta[pathname] || {}

    // Приоритет: props > page_meta > глобальные > дефолты
    const resolveTitle = props.title || page.title || settings.meta_title || 'Chat-Neuro — Нейросеть онлайн'
    const resolveDesc = props.description || page.description || settings.meta_description || ''
    const resolveKeywords = props.keywords || settings.meta_keywords || ''
    const resolveImage = props.ogImage || settings.og_image_url || '/logo-og.webp'
    const siteUrl = settings.site_url || 'https://chat-neuro.ru'
    const fullImage = resolveImage.startsWith('http') ? resolveImage : `${siteUrl}${resolveImage}`
    const canonical = props.canonical || (pathname === '/' ? siteUrl : `${siteUrl}${pathname}`)
    const updated = new Date().toISOString().replace('Z', '+03:00')

    // === Заменяем или создаём теги ===
    function setOrCreateMeta(name: string, content: string, property?: string) {
      if (!content) return
      let el: HTMLMetaElement | HTMLTitleElement | HTMLLinkElement | null = null

      if (name === 'title') {
        el = document.querySelector('title')
        if (el) (el as HTMLTitleElement).textContent = content
        else {
          const t = document.createElement('title')
          t.textContent = content
          document.head.appendChild(t)
        }
        return
      }

      if (name === 'canonical') {
        el = document.querySelector('link[rel="canonical"]')
        if (el) (el as HTMLLinkElement).href = content
        else {
          const l = document.createElement('link')
          l.rel = 'canonical'
          l.href = content
          document.head.appendChild(l)
        }
        return
      }

      // Meta-теги
      if (property) {
        // Ищем по property (og:*)
        el = document.querySelector(`meta[property="${property}"]`)
        if (el) (el as HTMLMetaElement).content = content
        else {
          const m = document.createElement('meta')
          m.setAttribute('property', property)
          m.content = content
          document.head.appendChild(m)
        }
      } else {
        // Ищем по name
        el = document.querySelector(`meta[name="${name}"]`)
        if (el) (el as HTMLMetaElement).content = content
        else {
          const m = document.createElement('meta')
          m.name = name
          m.content = content
          document.head.appendChild(m)
        }
      }
    }

    // === Заполняем ===
    setOrCreateMeta('title', resolveTitle)
    setOrCreateMeta('description', resolveDesc)
    setOrCreateMeta('canonical', canonical)

    // Open Graph
    setOrCreateMeta('og:locale', 'ru_RU', 'og:locale')
    setOrCreateMeta('og:type', 'website', 'og:type')
    setOrCreateMeta('og:title', resolveTitle, 'og:title')
    setOrCreateMeta('og:description', resolveDesc, 'og:description')
    setOrCreateMeta('og:url', canonical, 'og:url')
    setOrCreateMeta('og:updated_time', updated, 'og:updated_time')
    setOrCreateMeta('og:image', fullImage, 'og:image')
    setOrCreateMeta('og:image:secure_url', fullImage, 'og:image:secure_url')
    setOrCreateMeta('og:image:width', '800', 'og:image:width')
    setOrCreateMeta('og:image:height', '517', 'og:image:height')
    setOrCreateMeta('og:image:alt', resolveTitle, 'og:image:alt')
    setOrCreateMeta('og:image:type', 'image/webp', 'og:image:type')
    setOrCreateMeta('og:site_name', 'Chat-Neuro', 'og:site_name')

    // Twitter Card
    setOrCreateMeta('twitter:card', 'summary_large_image')
    setOrCreateMeta('twitter:title', resolveTitle)
    setOrCreateMeta('twitter:description', resolveDesc)
    setOrCreateMeta('twitter:image', fullImage)
    setOrCreateMeta('twitter:label1', 'Автор')
    setOrCreateMeta('twitter:data1', 'Chat-Neuro')
    setOrCreateMeta('twitter:label2', 'Время чтения')
    setOrCreateMeta('twitter:data2', '5 минут')

    // Keywords (если есть)
    if (resolveKeywords) {
      setOrCreateMeta('keywords', resolveKeywords)
    }

    // Robots
    if (props.noIndex) {
      setOrCreateMeta('robots', 'noindex, nofollow')
    } else {
      setOrCreateMeta('robots', 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large')
    }

    // === Head код (счётчики, аналитика) ===
    const headCode = (settings as any).head_code || ''
    // Удаляем старые вставки — все, с любым селектором
    document.querySelectorAll('.seo-head-ctr, .seo-head-html').forEach(el => el.remove())
    if (headCode.trim()) {
      // Вставляем noscript, комментарии и прочее (без script) в head
      const noScriptHtml = headCode.replace(/<script[\s\S]*?<\/script>/gi, '')
      if (noScriptHtml.trim()) {
        const wrapper = document.createElement('div')
        wrapper.className = 'seo-head-ctr'
        wrapper.innerHTML = noScriptHtml
        Array.from(wrapper.children).forEach(el => {
          el.classList.add('seo-head-html')
          document.head.appendChild(el)
        })
      }

      // Скрипты через createContextualFragment (создаёт живые, выполняемые элементы)
      const scriptOnly = headCode.replace(/<noscript[\s\S]*?<\/noscript>/gi, '')
      const frag = document.createRange().createContextualFragment(scriptOnly)
      frag.querySelectorAll('script').forEach(s => {
        if (document.querySelector('script.seo-head-ctr')) return // только один раз
        s.className = 'seo-head-ctr'
        if (s.src && document.querySelector('script[src="' + s.src + '"]')) return
        document.head.appendChild(s)
      })
    }

  }, [settings, pathname, props])

  return null
}
